<template>
  <div ref="zahlweise-form"
       class="zahlweise--form">

    <div class="section">
      <v-row no-gutters>

        <v-col cols="12"
               sm="3">
          <label class="dmb-form-text">Zahlungsart*
            <site-tooltip titel="SEPA-Lastschrift-Mandat">
              Vierteljährliche oder monatliche Zahlweise ist nur in Verbindung mit einem SEPA-Lastschrift-Mandat möglich.
            </site-tooltip>
          </label>
        </v-col>

        <v-col cols="12"
               sm="9">
          <v-radio-group v-model="rechnungszahler"
                         v-validate="{ required: aenderung }"
                         :hide-details="!aenderung"
                         :error-messages="errors.collect('Zahlungsart')"
                         name="Zahlungsart"
                         class="rechnungszahler-rb"
                         row>
            <v-radio :class="$vuetify.breakpoint.xsOnly ? 'pt16' : '' "
                     label="SEPA-Lastschrift-Mandat"
                     value="N"
                     color="primary" />
            <v-radio 
                     v-if="productLine!='YOLIG'"
                     :class="$vuetify.breakpoint.xsOnly ? 'pt16' : '' "
                     offset-md="3"
                     label="Rechnung"
                     value="J"
                     color="primary" />
            <v-radio v-if="maklerinkassoAllowed" 
                     :class="$vuetify.breakpoint.xsOnly ? 'pt16' : '' "
                     label="Maklerinkasso"
                     value="M"
                     color="primary" />
          </v-radio-group>
        </v-col>

      </v-row>

      <v-row no-gutters>

        <v-col cols="12"
               sm="3">
          <label class="dmb-form-text">Zahlweise*</label>
          <site-tooltip v-if="premiumMinimum > 0">
            Vierteljährliche oder monatliche Zahlweise ist erst ab einer Prämie von {{ $helpers.toGermanFloatString(premiumMinimum) }}&nbsp;€ möglich.
          </site-tooltip>
        </v-col>

        <v-col cols="12"
               sm="9">
          <v-radio-group v-model="zahlweise"
                         hide-details
                         class="zahlweise-rb"
                         name="zahlweise">

            <v-radio :class="$vuetify.breakpoint.xsOnly ? 'pt16' : '' "
                     value="1"
                     color="primary">
              <span slot="label">jährlich {{ $helpers.toGermanFloatString(totalPriceAlternatives.yearly) }}&nbsp;€</span>
            </v-radio>

            <v-radio value="2"
                     color="primary"
                     class="pt16">
              <span slot="label">halbjährlich {{ $helpers.toGermanFloatString(totalPriceAlternatives.halfYearly) }}&nbsp;€</span>
            </v-radio>

            <template v-if="rechnungszahler === 'N' || rechnungszahler === 'M'">

              <v-radio v-if="totalPriceAlternatives.quarterly >= premiumMinimum"
                       value="4"
                       color="primary"
                       class="pt16">
                <span slot="label">vierteljährlich {{ $helpers.toGermanFloatString(totalPriceAlternatives.quarterly) }}&nbsp;€</span>
              </v-radio>

              <v-radio v-if="totalPriceAlternatives.monthly >= premiumMinimum"
                       value="12"
                       color="primary"
                       class="pt16">
                <span slot="label">monatlich {{ $helpers.toGermanFloatString(totalPriceAlternatives.monthly) }}&nbsp;€</span>
              </v-radio>

            </template>

          </v-radio-group>
        </v-col>

      </v-row>

    </div>

    <bank-verbindung v-if="rechnungszahler === 'N'"
                     :state-path="statePath"
                     form-ref="konto-form" />

  </div>
</template>

<script>
import KeyTranslator from "@/components/helper/keyTranslatorMixin";
import ValidationMixin from "@/components/helper/ValidationMixin";
import BankVerbindung from "@/components/privatkundenrechner/Bankverbindung.vue";
import {mapTwoWay} from "@/store/twowayBindingMixin";
import twowayBinding from "@/store/twowayBindingMixin";
import {mapGetters} from "vuex";
import {RIGHTS} from '@/store/user';

export default {
  components: { BankVerbindung },
  mixins: [twowayBinding, ValidationMixin, KeyTranslator],
  props: {
    aenderung: {
      default: false,
      type: Boolean
    },
    formRef: {
      type: String,
      required: true
    },
    sensibleDefaults: {
      default: true,
      type: Boolean
    }
  },
  computed: {
    maklerinkassoAllowed() {
      return this.$store.getters.checkAuthorization(RIGHTS.MAKLERINKASSO);
    },
    ...mapGetters([
      "premiumMinimum"
    ]),
    ...mapTwoWay([
      "zahlweise",
      "rechnungszahler",
      "sus",
      "productLine"
    ]),
    totalPriceAlternatives() {
      const customPraemienGetter = this.$store.getters.customPraemien(this.statePath);
      const monthly = customPraemienGetter("12").total;
      const quarterly = customPraemienGetter("4").total;
      const halfYearly = customPraemienGetter("2").total;
      const yearly = customPraemienGetter("1").total;
      return { monthly, quarterly, halfYearly, yearly };
    }
  },
  watch: {
    zahlweise: function (newval, oldval) {
      if (this.sensibleDefaults) {
        if (newval === null) {
          this.zahlweise = oldval;
        }
      }
    },
    rechnungszahler: function (newval, oldval) {
      if (this.sensibleDefaults) {
        if (newval === null) {
          this.rechnungszahler = oldval;
          return;
        }
        if (oldval === "N" && newval === "J") {
          if (this.zahlweise === "12" || this.zahlweise === "4") {
            this.zahlweise = "1";
          }
        }
      }
    },
    totalPriceAlternatives() {
      if (this.zahlweise === "12" && this.totalPriceAlternatives.monthly < this.premiumMinimum) {
        this.zahlweise = this.totalPriceAlternatives.quarterly < this.premiumMinimum ? "2" : "4";
      }
      if (this.zahlweise === "4" && this.totalPriceAlternatives.quarterly < this.premiumMinimum) {
        this.zahlweise = "2";
      }
    }
  }
};
</script>

<style>
.zahlweise--form .section {
  margin-bottom: -16px !important;
  overflow: auto;
}

.zahlweise--form .v-icon {
  cursor: pointer;
}

.zahlweise--form .pt16 {
  padding: 16px 0 0 0 !important;
}

.zahlweise--form .dmb-form-text {
  font-size: 16px;
}

.zahlweise--form .rechnungszahler-rb, .zahlweise-rb {
  padding-top: 0 !important;
}

.zahlweise--form .v-input--radio-group__input .v-radio:not(:last-child) {
    margin-right: 48px!important;
}
</style>

